import { query } from '../../network/fetch'
import { get } from '../../utilities'
import schemaBuilder from './Schema'

const graphql = {

    components: null,

    install (components) {

        this.components = components

        const { Model, Actions } = components

        Actions.$query = this.query.bind(this)
        Actions.$mutate = this.mutate.bind(this)

        Model.$cloneOrNew = function(id) {
            return new this(this.find(id) || new this)
        }
        Model.$fetch = function(config = {}) {
            return this.dispatch('$query', config)
        }
        Model.$fetchAll = function(variables = {}) {
            return this.dispatch('$query', { graphql: 'all', variables })
        }
        Model.$fetchOne = function(variables = {}) {
            return this.dispatch('$query', { graphql: 'one', variables })
        }
        Model.$mutate = function(config = {}) {
            return this.dispatch('$mutate', config)
        }
        Model.$create = function(variables = {}) {
            return this.dispatch('$mutate', { graphql: 'create', variables })
        }
        Model.$update = function(variables = {}) {
            return this.dispatch('$mutate', { graphql: 'update', variables })
        }
        Model.$remove = function(variables = {}) {
            return this.dispatch('$mutate', { graphql: 'remove', variables })
        }

        const model = components.Model.prototype
        model.$create = async function() {
            return this.$dispatch('$mutate', { 
                graphql: 'create',
                variables: this
            })
        }
        model.$update = async function() {
            return this.$dispatch('$mutate', { 
                graphql: 'update', 
                variables: this
            })
        }
        model.$remove = async function() {
            const resp = await this.$dispatch('$mutate', { 
                graphql: 'remove',
                variables: this
            })
            this.$delete()
            return resp
        }

        model.$relate = async function(
            id, 
            field,
            type,
            flip
        ) {
            const relation = this.$fields()[field]

            var variables = {}
            variables[relation.relatedPivotKey] = id
            variables[relation.foreignPivotKey] = this.id

            var parameters = {}
            parameters[relation.relatedPivotKey] = 'Int!'
            parameters[relation.foreignPivotKey] = 'Int!'
            
            if (flip) {
                return await this.$dispatch('$mutate', {
                    graphql: field,
                    variables,
                    style: { type, parameters }
                })
            }
            else {
                const related = components.Query.getModel(relation.related.entity)
                return await related.dispatch('$mutate', {
                    graphql: field,
                    variables,
                    style: { type, parameters }
                })
            }
        }

        model.$attach = async function(
            id, 
            field,
            type,
            flip = false
        ) {
            const resp = await this.$relate(
                id, 
                field, 
                type,
                flip
            )
            const data = { id: this.id }
            data[field] = [ resp.data ]
            return this.constructor.insertOrUpdate({ data })
        }

        model.$detach = async function(
            id, 
            field,
            type,
            flip = false
        ) {
            const relation = this.$fields()[field]
            const pivot = components.Query.getModel(relation.pivot.entity)
            await this.$relate(
                id, 
                field, 
                type,
                flip
            )
            const el = pivot.query().find(this.id + '_' + id)
            if (el) el.$delete()
            return { id }
        }
    },

    async fetch(builder, action) {
        const schema = builder.build(action)
        const type = builder.schemaName()
        const response = await query(schema)
        const raw = get(response.data, type)
        return { raw, headers: response.headers }
    },

    async query({ state, dispatch }, config) {
        const model = this.components.Query.getModel(state.$name)
        const builder = schemaBuilder.init(model,
            this.components, 
            config)
        const { raw, headers } = await this.fetch(builder, 'query')
        const data = await this.upsertAndFetch(dispatch, 
            model, 
            raw)
        return { data, raw, headers }
    },

    async mutate({ state, dispatch }, config) {
        const model = this.components.Query.getModel(state.$name)
        const builder = schemaBuilder.init(model,
            this.components, 
            config)
        const { raw, headers } = await this.fetch(builder, 'mutation')
        const data = await this.upsertAndFetch(dispatch, 
            model, 
            raw)
        return { data, raw, headers }
    },

    async upsertAndFetch(dispatch, 
        model, 
        data) {
        await dispatch('insertOrUpdate', this.wrapData(data))
        
        const unwrapped = this.unwrapData(data)
        if (unwrapped instanceof Array) {
            const ids = this.unwrapData(data).map((item) => item.id)
            return model.query().withAll().whereIdIn(ids).get()
        }
        else {
            return model.query().withAll().where('id', data.id).first()
        }
    },

    wrapData(value) {
        return value.data ? value : { data: value }
    },

    unwrapData(value) {
        return value.data ? value.data : value
    }
}

export default graphql